import {ITOCItem} from '../../../../components/TableOfContents/TableOfContents';
import {ISpecialtyGroupingsData} from './types';

/**
 * Generates a URL-safe ID from a title by converting to lowercase
 * and replacing non-alphanumeric characters with hyphens
 */
export const generateSectionId = (title: string): string => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
};

/**
 * Builds a dynamic table of contents from specialty grouping API data
 *
 * Creates a hierarchical table of contents structure with:
 * - Four main chapters (Introduction, Summary, County Level Results, Caveats)
 * - Dynamic specialty subsections under County Level Results based on API data
 * - Alphabetically sorted specialties combining both provider and facility types
 *
 * @param standardType - The standard type (e.g., 'MA', 'QHP')
 * @param standardVersion - The standard version (e.g., '2026.1')
 * @param specialtyGroupings - The specialty groupings data from API
 * @returns Array of TOC items with dynamic specialty subsections
 *
 * @example
 * const toc = buildDynamicTableOfContents('MA', '2026.1', specialtyData);
 * // Returns structure with 4 chapters and dynamic specialty list
 */
export const buildDynamicTableOfContents = (
    standardType: string,
    standardVersion: string,
    specialtyGroupings: ISpecialtyGroupingsData | null
): ITOCItem[] => {
    const year = standardVersion.split('.')[0];

    // Combine provider and facility specialties, sort alphabetically
    const allSpecialties = [
        ...(specialtyGroupings?.providerSpecialties || []),
        ...(specialtyGroupings?.facilitySpecialties || [])
    ];
    // Build specialty children array with Field Definitions first
    const specialtyChildren: ITOCItem[] = [
        {title: 'Field Definitions'}
    ];

    // Add each specialty as a TOC item
    allSpecialties.forEach(specialty => {
        specialtyChildren.push({
            title: specialty.specialty_name
        });
    });

    return [
        {
            title: 'I. Introduction',
            children: [
                {title: 'Specialty Types'},
                {title: 'County Designation'},
                {title: `PY${year} ${standardType} Network Access Standards`}
            ]
        },
        {
            title: 'II. Summary of Network Access Results'
        },
        {
            title: 'III. County Level Results by Specialty',
            children: specialtyChildren
        },
        {
            title: 'IV. Caveats'
        }
    ];
};
